import LazyLoad from 'vanilla-lazyload';
import { forEachElement } from './common/utils/dom';
import { onDOMContentLoaded, onOnce } from './common/utils/domEvents';
import loadComponentByIntersectionObserver from './common/utils/loadComponentByIntersectionObserver';
import getFrontConfig from './common/getFrontConfig';
import { createIntersectionObserverHandler, createIntersectionObserver } from './common/utils/intersectionObserver';

const FRONT_CONFIG = getFrontConfig();
// eslint-disable-next-line
__webpack_public_path__ = FRONT_CONFIG.urls.staticJs;
const getHomeContainer = () => document.querySelector('.the-home');
const getVideoContainer = () => document.querySelector('.the-home__background-container--video');
const getVideoEle = () => getVideoContainer()?.querySelector('video');
const getVideoModules = () => document.querySelectorAll('.the-home-module video');
const isMatchMedia = (maxWidth) => window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
let lastWidth = window.innerWidth;
const { isCampaignPD } = window;
const { thresholdsLazyLoad } = FRONT_CONFIG.home;
const thresholdsVerticalLazyLoad = (isCampaignPD
  ? thresholdsLazyLoad?.verticalImgCampaign
  : thresholdsLazyLoad?.verticalImgHome)
  || '200% 150%';
const thresholdsHorizontalLazyLoad = (isCampaignPD
  ? thresholdsLazyLoad?.horizontalImgCampaign
  : thresholdsLazyLoad?.horizontalImgHome)
  || '200% 150%';

const showBackgroundVideo = () => {
  setTimeout(() => getVideoContainer().classList.add('transition-ap8--show'), 500);
};

const getBackgroundVideoOptions = (videoEle) => {
  const { videoBreakpoint, srcMobile, srcDesktop } = videoEle.dataset;
  const posterMobile = videoEle.getAttribute('poster-mobile');
  const posterDesktop = videoEle.getAttribute('poster-desktop');
  const src = isMatchMedia(videoBreakpoint) ? srcMobile : srcDesktop;
  const poster = isMatchMedia(videoBreakpoint) ? posterMobile : posterDesktop;
  return { src, poster, bp: videoBreakpoint };
};

let playPromise;
const setBackgroundVideoOptions = (videoEle, options, homeVideo = false) => {
  videoEle.pause();
  if (options.poster && videoEle.poster !== options.poster) {
    videoEle.setAttribute('poster', options.poster);
  }

  if (options.src && videoEle.currentSrc !== options.src) {
    videoEle.setAttribute('src', options.src);
  }

  if (homeVideo) onOnce(videoEle, 'play', showBackgroundVideo);
  videoEle.load();
  // videoEle.play();

  if (playPromise !== undefined) {
    playPromise.then(() => {
      videoEle.pause();
      playPromise = videoEle.play();
    });
  }
};

const callbackEnter = (element) => {
  const oneLL = new LazyLoad({
    container: element,
    elements_selector: '.the-home__image-base.lazy',
    class_loading: 'the-home__image-base--loading',
    class_loaded: 'the-home__image-base--loaded',
    thresholds: thresholdsHorizontalLazyLoad,
  });
  oneLL.loadAll();
  window.theHomeLazyLoad2.push(oneLL);
};

const initAnimationLoader = () => {
  const className = 'the-home-animations';
  const classNameActive = `${className}--active`;
  const callback = (entry, observer) => {
    const { target } = entry;
    if (!target?.classList?.contains(className)) observer.disconnect();
    target?.classList?.add(classNameActive);
  };
  const callbackLeave = (entry, observer) => {
    const { target } = entry;
    if (!target?.classList?.contains(className)) observer.disconnect();
    target?.classList?.remove(classNameActive);
  };
  createIntersectionObserver(
    `.${className}`,
    createIntersectionObserverHandler({
      inCallback: callback,
      outCallback: callbackLeave,
    }),
  );
};

const initBackgroundVideo = () => {
  forEachElement(
    getVideoModules(),
    (videoModule) => {
      const optToSetModule = getBackgroundVideoOptions(videoModule);
      setBackgroundVideoOptions(videoModule, optToSetModule);
    },
  );

  const videoEle = getVideoEle();
  if (!videoEle) return;
  const optToSet = getBackgroundVideoOptions(videoEle);
  setBackgroundVideoOptions(videoEle, optToSet, true);
};

const initComponentsLoaders = () => {
  const glide = () =>
    import(/* webpackChunkName: "glide.js", webpackMode: "lazy-once" */ './components/home/glide');
  const categoryFeatured = () =>
    import(/* webpackChunkName: "categoryFeatured.js", webpackMode: "lazy-once" */ './components/home/categoryFeatured');
  const customSlider = () =>
    import(/* webpackChunkName: "customSlider.js", webpackMode: "lazy-once" */ './components/home/customSlider');
  const shopTheLook = () =>
    import(/* webpackChunkName: "shopTheLook.js", webpackMode: "lazy-once" */ './components/home/shopTheLook');
  const targets = {
    '.the-home-module .glide': { component: glide },
    '.the-home-module .the-home-category-featured': { component: categoryFeatured },
    '.the-home-module .the-home-product-carousel': { component: customSlider },
    '.the-home-module .the-home-shop-the-look': {
      component: shopTheLook,
      options: {
        rootMargin: '100% 0px',
      },
    },
  };
  Object.entries(targets).forEach(([target, { component, options }]) => {
    loadComponentByIntersectionObserver(target, component, options);
  });
};

const removeInvisibleSlides = () => {
  const bpFigure = 768;
  const bpType = isMatchMedia(bpFigure) ? 'mobile' : 'desktop';
  const slideClassQuery = 'div[class*="slide"]';

  // filter for images with standard BP
  const figureFilter = `${slideClassQuery} figure[data-include-breakpoint="${bpFigure}"][data-include-${bpType}="false"]`;
  const figures = getHomeContainer().querySelectorAll(figureFilter);
  if (figures) {
    forEachElement(figures, (figure) => {
      const parent = figure.closest(slideClassQuery);
      parent.parentNode.removeChild(parent);
    });
  }

  // filter for images with different BP
  const figureFilterDif = `
    ${slideClassQuery} figure:not([data-include-breakpoint="${bpFigure}"])[data-include-mobile="false"],
    ${slideClassQuery} figure:not([data-include-breakpoint="${bpFigure}"])[data-include-desktop="false"]
  `;
  const figuresDif = getHomeContainer().querySelectorAll(figureFilterDif);
  if (!figuresDif) return;
  forEachElement(figuresDif, (figure) => {
    const { includeBreakpoint, includeMobile, includeDesktop } = figure.dataset;
    const parent = figure.closest(slideClassQuery);
    const isMobileHidden = isMatchMedia(includeBreakpoint) && includeMobile === 'false';
    const isDesktopHidden = !isMatchMedia(includeBreakpoint) && includeDesktop === 'false';
    if (isMobileHidden || isDesktopHidden) parent.parentNode.removeChild(parent);
  });
};

function onHomeResize() {
  if (lastWidth === window.innerWidth) return;
  lastWidth = window.innerWidth;
  initBackgroundVideo();
}

function onHomeVisibilitychange() {
  if (document.visibilityState === 'visible') initBackgroundVideo();
}

function removeTheHomeEventsListeners() {
  window.removeEventListener('resize', onHomeResize);
  document.removeEventListener('visibilitychange', onHomeVisibilitychange);
}

export default {
  removeTheHomeEventsListeners,
};

onDOMContentLoaded(() => {
  initComponentsLoaders();
  initAnimationLoader();
  initBackgroundVideo();
  window.addEventListener('resize', onHomeResize);
  // IOS fix
  document.addEventListener('visibilitychange', onHomeVisibilitychange);
  window.theHomeLazyLoad = new LazyLoad({
    elements_selector: '.the-home__image-base.lazy',
    class_loading: 'the-home__image-base--loading',
    class_loaded: 'the-home__image-base--loaded',
    thresholds: thresholdsVerticalLazyLoad,
  });
  window.theHomeBackgroundsLazy = new LazyLoad({
    elements_selector: '[data-bg-lazy]',
    threshold: 0,
    unobserve_entered: true,
    callback_enter: (element) => element.classList.add(`${element.dataset.bgLazy}--bg-lazy`),
  });
  window.theHomeLazyLoad2 = [];
  // eslint-disable-next-line no-unused-vars
  const lazyLazy = new LazyLoad({
    elements_selector: '.custom-slider__items',
    unobserve_entered: true,
    callback_enter: callbackEnter,
  });
  removeInvisibleSlides();
});
